/**
 * قالب‌بندی عدد، درصد و تاریخ فارسی.
 *
 * همه‌چیز روی `Intl` سوار است، نه نگاشت دستی ارقام. دلیل: `Intl` در پلتفرم
 * است، تقویم جلالی و ارقام فارسی را با هم درست می‌دهد، و نگاشت دستی ارقام یک
 * اشتباه رایج است که روی اعداد اعشاری، جداکننده‌ی هزارگان و علامت منفی
 * می‌شکند.
 */

const FA = 'fa-IR'

/**
 * عدد با ارقام فارسی.
 *
 * `useGrouping: false` پیش‌فرض است چون بیشتر اعداد این رابط کوچک‌اند (امتیاز،
 * شمارش، درصد) و جداکننده‌ی هزارگان روی آن‌ها فقط شلوغی می‌سازد.
 */
export const fa = (value: number, options?: Intl.NumberFormatOptions): string =>
  new Intl.NumberFormat(FA, { useGrouping: false, ...options }).format(value)

/** عدد بزرگ با جداکننده‌ی هزارگان فارسی. برای شمارش توکن و حجم فایل. */
export const faCount = (value: number): string =>
  new Intl.NumberFormat(FA, { useGrouping: true }).format(value)

/** درصد. مقدار ورودی از صفر تا صد است، نه صفر تا یک. */
export const faPercent = (value: number): string => `${fa(Math.round(value))}٪`

/** امتیاز اعشاری بازیابی، با سه رقم اعشار. */
export const faScore = (value: number): string =>
  fa(value, { minimumFractionDigits: 3, maximumFractionDigits: 3 })

/**
 * حجم فایل خوانا.
 *
 * `Intl` واحد `byte` را با `unitDisplay` پشتیبانی می‌کند، اما مقیاس‌بندی
 * (بایت → کیلوبایت) را خودش انجام نمی‌دهد؛ آن یک خط حساب است.
 */
export const faBytes = (bytes: number): string => {
  if (bytes < 1024) {
    return `${fa(bytes)} بایت`
  }
  const kilobytes = bytes / 1024
  if (kilobytes < 1024) {
    return `${fa(Math.round(kilobytes))} کیلوبایت`
  }
  return `${fa(kilobytes / 1024, { maximumFractionDigits: 1 })} مگابایت`
}

/**
 * تاریخ و ساعت شمسی.
 *
 * تقویم از `fa-IR` می‌آید که پیش‌فرضش جلالی است؛ تبدیل دستی لازم نیست.
 * ورودی رشته‌ی ISO است، همان چیزی که backend می‌فرستد.
 */
export const faDateTime = (iso: string): string => {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return '—'
  }
  return new Intl.DateTimeFormat(FA, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date)
}

export const faDate = (iso: string): string => {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return '—'
  }
  return new Intl.DateTimeFormat(FA, { dateStyle: 'medium' }).format(date)
}

/**
 * فاصله‌ی زمانی نسبی، مثل «۳ دقیقه پیش».
 *
 * `Intl.RelativeTimeFormat` متن را می‌سازد؛ فقط انتخاب واحد مناسب دست ماست.
 */
export const faRelative = (iso: string): string => {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return '—'
  }
  const seconds = (date.getTime() - Date.now()) / 1000
  const formatter = new Intl.RelativeTimeFormat(FA, { numeric: 'auto' })
  const steps: [Intl.RelativeTimeFormatUnit, number][] = [
    ['second', 60],
    ['minute', 60],
    ['hour', 24],
    ['day', 30],
    ['month', 12],
  ]
  let value = seconds
  for (const [unit, size] of steps) {
    if (Math.abs(value) < size) {
      return formatter.format(Math.round(value), unit)
    }
    value /= size
  }
  return formatter.format(Math.round(value), 'year')
}

/** متن خالی را به خط تیره تبدیل می‌کند تا جدول ردیف تهی نداشته باشد. */
export const orDash = (value: string | null | undefined): string =>
  value === null || value === undefined || value.trim() === '' ? '—' : value

/** برش متن بلند با نقطه‌چین. */
export const truncate = (value: string, limit: number): string =>
  value.length <= limit ? value : `${value.slice(0, limit)}…`

/**
 * دانلود یک `Blob` در مرورگر.
 *
 * اینجاست چون هر سه مسیر خروجی‌گیری (Markdown، JSON، قالب xlsx) به آن نیاز
 * دارند و تکرارش در سه جا، سه فرصت برای فراموش کردن `revokeObjectURL` بود.
 */
export const saveBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.append(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}
